import { useState, useContext, useEffect } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Box, TextField, IconButton, Autocomplete } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import {
  numToCurrency,
  convertAllDates,
  numToCurrencyDecimal,
  hstRebate,
  effectivePriceAfterRebate,
  getOutdoorSize,
  getOutdoorType,
} from '../../utils/Functions';
import { UnitContext } from '../../context/UnitContext';
import { netHST } from '../../utils/Functions';
import { useAppDispatch } from '../../app/hooks';
import { showSuccessSnackbar, showErrorSnackbar } from '../../features/snackbar/snackbarSlice';
import { useSelector } from 'react-redux';
import { selectUser } from '../../features/auth/authSlice';
import { useCreateActivity } from '../../features/activity/activityHooks';
import { selectProject } from '../../features/project/projectSlice';
import { IUnit, IUnitHistory } from '../../types/unit';
import Unit from '../common/Unit';

const UnitInfo = () => {
  const storeDispatch = useAppDispatch();
  const user = useSelector(selectUser);
  const project = useSelector(selectProject);
  const createActivity = useCreateActivity();
  const { unit, filteredDeal, setUnit } = useContext(UnitContext);
  const [units, setUnits] = useState<IUnit[]>([]);
  const [edit, setEdit] = useState<boolean>(false);

  // Commercial units are never net of HST (matches Current Price (Net) below)
  const isNetPrice = project.netHst && unit.type !== 'commercial';
  const netLabel = isNetPrice ? ' (Net)' : '';
  const displayPrice = (price: number) => (isNetPrice ? netHST(price, project) : price);
  const [projectName, setProjectName] = useState<string>(unit.projectName ? unit.projectName : '');
  const [level, setLevel] = useState<string>(unit.level ? unit.level : '');
  const [rental, setRental] = useState<string | null>(unit.rental ? unit.rental : null);
  const [unitNumber, setUnitNumber] = useState<string>(unit.unit ? unit.unit : '');
  const [size, setSize] = useState<string>(unit.size ? unit.size.toString() : '');
  const [modelType, setModelType] = useState<string>(unit.modelType ? unit.modelType : '');
  const [unitType, setUnitType] = useState<string>(unit.unitType ? unit.unitType : '');
  const [bathroom, setBathroom] = useState<string>(unit.bathroom ? unit.bathroom.toString() : '');
  const [exposure, setExposure] = useState<string>(unit.exposure ? unit.exposure : '');
  const [outdoorSize, setOutdoorSize] = useState<string>(unit.outdoor?.[0]?.size ? unit.outdoor[0].size.toString() : '');
  const [outdoorType, setOutdoorType] = useState<string>(unit.outdoor?.[0]?.name ? unit.outdoor[0].name : '');
  const [maintenanceFee, setMaintenanceFee] = useState<string>(unit.maintenanceFee ? unit.maintenanceFee : '');
  const [firstTentativeOccupancy, setFirstTentativeOccupancy] = useState<Date | null>(
    unit.firstTentativeOccupancy ? unit.firstTentativeOccupancy : null
  );
  const [finalTentativeOccupancy, setFinalTentativeOccupancy] = useState<Date | null>(
    unit.finalTentativeOccupancy ? unit.finalTentativeOccupancy : null
  );
  const [firmOccupancy, setFirmOccupancy] = useState<Date | null>(unit.firmOccupancy ? unit.firmOccupancy : null);
  const [outsideOccupancy, setOutsideOccupancy] = useState<Date | null>(unit.outsideOccupancy ? unit.outsideOccupancy : null);

  useEffect(() => {
    setLevel(unit.level);
    setUnitNumber(unit.unit);
    setProjectName(unit.projectName);
    setSize(unit.size);
    setRental(typeof unit.rental !== 'undefined' && unit.rental !== null ? unit.rental : '');
    setModelType(unit.modelType);
    setUnitType(unit.unitType);
    setBathroom(unit.bathroom);
    setExposure(unit.exposure);
    setOutdoorSize(unit.outdoor?.[0]?.size ? unit.outdoor[0].size.toString() : '');
    setOutdoorType(unit.outdoor?.[0]?.name ? unit.outdoor[0].name : '');
    setFirstTentativeOccupancy(unit.firstTentativeOccupancy);
    setFinalTentativeOccupancy(unit.finalTentativeOccupancy);
    setFirmOccupancy(unit.firmOccupancy);
    setOutsideOccupancy(unit.outsideOccupancy);
    setMaintenanceFee(unit.maintenanceFee);
  }, [edit, unit]);

  useQuery(UNITS, {
    skip: project._id !== '65bd404201d843802bb37a51',
    variables: { project: project._id },
    fetchPolicy: 'cache-first',
    onCompleted: (data) => {
      setUnits(data.getUnits);
    },
  });

  const [updateUnit] = useMutation(UPDATEUNIT, {
    onCompleted: (data) => {},
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  // Gross total. On netHst projects HST is added to the options (basePrice is already gross), matching the create-deal summary.
  const getTotalPrice = () => {
    const optionsTotal = filteredDeal.options.reduce((a: any, b: any) => a + b.amount, 0);
    return filteredDeal.basePrice + (project.netHst ? optionsTotal * 1.13 : optionsTotal);
  };

  // Net of the same price shown on the Deal/Unit Price (Net) line, plus the deal's options at full value
  const getTotalNetPrice = () => {
    const dealClosed = filteredDeal && (filteredDeal.cancelled.dateCancelled || filteredDeal.rescission.dateRescinded);
    const price = dealClosed ? filteredDeal.basePrice : unit.basePrice;
    const netPrice = unit.type === 'commercial' ? price : netHST(price, project);
    const optionsTotal = filteredDeal ? filteredDeal.options.reduce((a: any, b: any) => a + b.amount, 0) : 0;
    return netPrice + optionsTotal;
  };

  const handleSaveUnit = (e: any) => {
    e.preventDefault();
    let history: any[] = [];
    if (level !== unit.level) {
      history.push({
        type: 'Level',
        description: `Level has changed from ${unit.level} to ${level}`,
        timestamp: new Date(),
        user: user._id,
      });
    }
    if (unit.unit !== unitNumber) {
      history.push({
        type: 'Unit',
        description: `Unit has been changed ${unit.unit} to ${unitNumber}`,
        timestamp: new Date(),
        user: user._id,
      });
    }
    if (unit.rental !== rental) {
      history.push({
        type: 'Rental',
        description: `Rental has been changed ${unit.rental} to ${rental}`,
        timestamp: new Date(),
        user: user._id,
      });
    }
    if (unit.size.toString() !== size.toString()) {
      history.push({
        type: 'Size',
        description: `Size has been changed ${unit.size.toString()} to ${size}`,
        timestamp: new Date(),
        user: user._id,
      });
    }
    if (unit.modelType !== modelType) {
      history.push({
        type: 'Model Type',
        description: `Model Type has been changed ${unit.modelType} to ${modelType}`,
        timestamp: new Date(),
        user: user._id,
      });
    }
    if (unit.unitType !== unitType) {
      history.push({
        type: 'Unit Type',
        description: `Unit Type has been changed ${unit.unitType} to ${unitType}`,
        timestamp: new Date(),
        user: user._id,
      });
    }
    if (unit.bathroom.toString() !== bathroom.toString()) {
      history.push({
        type: 'Bathroom',
        description: `Bathroom has been changed ${unit.bathroom} to ${bathroom}`,
        timestamp: new Date(),
        user: user._id,
      });
    }
    if (unit.exposure !== exposure) {
      history.push({
        type: 'Exposure',
        description: `Exposure has been changed ${unit.exposure} to ${exposure}`,
        timestamp: new Date(),
        user: user._id,
      });
    }
    const existingOutdoorSize = unit.outdoor?.[0]?.size ? unit.outdoor[0].size.toString() : '';
    const existingOutdoorType = unit.outdoor?.[0]?.name ? unit.outdoor[0].name : '';
    if (existingOutdoorSize !== outdoorSize.toString()) {
      history.push({
        type: 'Outdoor Size',
        description: `Outdoor Size has been changed ${existingOutdoorSize} to ${outdoorSize}`,
        timestamp: new Date(),
        user: user._id,
      });
    }
    if (existingOutdoorType !== outdoorType) {
      history.push({
        type: 'Outdoor Type',
        description: `Outdoor Type has been changed ${existingOutdoorType} to ${outdoorType}`,
        timestamp: new Date(),
        user: user._id,
      });
    }
    if (unit.maintenanceFee !== maintenanceFee) {
      history.push({
        type: 'Maintenance Fee',
        description: `Maintenance Fee has been changed ${unit.maintenanceFee} to ${maintenanceFee}`,
        timestamp: new Date(),
        user: user._id,
      });
    }
    if (convertAllDates(new Date(unit.firstTentativeOccupancy), 'PP') !== convertAllDates(new Date(firstTentativeOccupancy!), 'PP')) {
      history.push({
        type: 'First Tentative Occupancy',
        description: `First Tentative Occupancy has been changed ${convertAllDates(
          unit.firstTentativeOccupancy,
          'PP'
        )} to ${convertAllDates(firstTentativeOccupancy, 'PP')}`,
        timestamp: new Date(),
        user: user._id,
      });
    }
    if (convertAllDates(new Date(unit.finalTentativeOccupancy), 'PP') !== convertAllDates(new Date(finalTentativeOccupancy!), 'PP')) {
      history.push({
        type: 'Final Tentative Occupancy',
        description: `Final Tentative Occupancy has been changed ${convertAllDates(
          unit.finalTentativeOccupancy,
          'PP'
        )} to ${convertAllDates(finalTentativeOccupancy, 'PP')}`,
        timestamp: new Date(),
        user: user._id,
      });
    }
    if (convertAllDates(new Date(unit.firmOccupancy), 'PP') !== convertAllDates(new Date(firmOccupancy!), 'PP')) {
      history.push({
        type: 'Firm Occupancy',
        description: `Firm Occupancy has been changed ${convertAllDates(unit.firmOccupancy, 'PP')} to ${convertAllDates(
          firmOccupancy,
          'PP'
        )}`,
        timestamp: new Date(),
        user: user._id,
      });
    }
    if (convertAllDates(new Date(unit.outsideOccupancy), 'PP') !== convertAllDates(new Date(outsideOccupancy!), 'PP')) {
      history.push({
        type: 'Outside Occupancy',
        description: `Outside Occupancy has been changed ${convertAllDates(unit.outsideOccupancy, 'PP')} to ${convertAllDates(
          outsideOccupancy,
          'PP'
        )}`,
        timestamp: new Date(),
        user: user._id,
      });
    }
    if (unit.projectName !== projectName && project._id === '65bd404201d843802bb37a51') {
      history.push({
        type: 'Project Name',
        description: `Project Name has been changed ${unit.projectName} to ${projectName}`,
        timestamp: new Date(),
        user: user._id,
      });
    }
    if (!history.length) return storeDispatch(showErrorSnackbar('No Changes Made'));
    updateUnit({
      variables: {
        _id: unit._id,
        record: {
          level: level,
          unit: unitNumber,
          projectName: projectName,
          size: parseInt(size, 10),
          rental: rental ? parseInt(rental, 10) : null,
          modelType: modelType,
          unitType: unitType,
          bathroom: parseInt(bathroom, 10),
          exposure: exposure,
          outdoor: outdoorType || outdoorSize ? [{ name: outdoorType, size: parseInt(outdoorSize) || 0 }] : [],
          maintenanceFee: parseFloat(maintenanceFee),
          firstTentativeOccupancy: firstTentativeOccupancy,
          finalTentativeOccupancy: finalTentativeOccupancy,
          firmOccupancy: firmOccupancy,
          outsideOccupancy: outsideOccupancy,
          history: [
            ...unit.history.map((history: IUnitHistory) => {
              return {
                ...history,
                user: history.user ? history.user._id : null,
              };
            }),
            ...history,
          ],
        },
      },
    }).then((res) => {
      if (history.length) {
        createActivity({
          project: project._id,
          user: user._id,
          deal: null,
          title: `Unit Update ${unit.suite}`,
          content: history.map((history: any) => history.description).join(', '),
        });
      }
      storeDispatch(showSuccessSnackbar('Unit Updated!'));
      setUnit(res.data.unitUpdateById.record);
      setEdit(false);
    });
  };

  const handleDropdown = (e: any) => {
    if (e.target.name === 'exposure') {
      setExposure(e.target.value);
    }
    if (e.target.name === 'outdoorType') {
      setOutdoorType(e.target.value);
    }
    if (e.target.name === 'unitType') {
      setUnitType(e.target.value);
    }
  };

  return (
    <Box
      sx={{
        padding: '20px',
        width: '200px',
        border: '1px solid #000',
        position: 'sticky',
        top: '20px',
        '@media (max-width: 600px)': {
          width: '100%',
        },
      }}
    >
      {user.type === 'Admin' || user.type === 'Manager' ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <EditIcon onClick={() => setEdit(!edit)} sx={{ cursor: 'pointer', ml: 1 }} fontSize="small" />
          {edit ? (
            <IconButton sx={{ p: 0 }} type="submit" form="unitForm">
              <SaveIcon sx={{ cursor: 'pointer', color: 'success.main', ml: 1 }} fontSize="small" />
            </IconButton>
          ) : null}
        </Box>
      ) : null}
      {!edit ? (
        <Box>
          {project._id === '65bd404201d843802bb37a51' ? (
            <p>
              Project: <strong>{unit.projectName}</strong>
            </p>
          ) : null}
          <p>
            Status: <strong>{unit.status}</strong>
          </p>
          <p>
            Opening Day Price{netLabel}: <strong>{numToCurrency.format(displayPrice(unit.originalPrice))}</strong>
          </p>
          <div style={{ marginBottom: 2 }}>
            <div>Unit Price:</div>
            {filteredDeal && (filteredDeal.cancelled.dateCancelled || filteredDeal.rescission.dateRescinded) ? (
              <p style={{ marginTop: 0}}>
                <strong>{numToCurrency.format(filteredDeal.basePrice)}</strong>
              </p>
            ) : (
              <p style={{ marginTop: 0}}>
                <strong>{numToCurrency.format(unit.basePrice)}</strong>
              </p>
            )}
          </div>
          {filteredDeal ? (
            <>
              {unit.basePrice !== filteredDeal.totalPrice ? (
                <Box sx={{ mb: 2 }}>
                  <div>Total Price:</div>
                  <p style={{ marginTop: 0}}>
                    <strong>{numToCurrency.format(getTotalPrice())}</strong>
                  </p>
                </Box>
              ) : null}
              {filteredDeal.options.map((option: any, index: number) => {
                if (option.purchaseAmount) {
                  return (
                    <Box sx={{ mb: 2 }} key={index}>
                      <div>{option.name}:</div>
                      <p style={{ marginTop: 0}}>
                        <strong>
                          {option.purchaseAmount}x - {numToCurrency.format(option.amount)}
                        </strong>
                      </p>
                    </Box>
                  );
                } else return null;
              })}
            </>
          ) : null}
          <Box sx={{ mt: 1 }}>
            {filteredDeal && (filteredDeal.cancelled.dateCancelled || filteredDeal.rescission.dateRescinded) ? (
              <p>
                Deal Price (Net):{' '}
                <strong>
                  {unit.type === 'commercial'
                    ? numToCurrency.format(filteredDeal.basePrice)
                    : numToCurrency.format(netHST(filteredDeal.basePrice, project))}
                </strong>
              </p>
            ) : (
              <p>
                Unit Price (Net):{' '}
                <strong>
                  {unit.type === 'commercial' ? numToCurrency.format(unit.basePrice) : numToCurrency.format(netHST(unit.basePrice, project))}
                </strong>
              </p>
            )}
            <p>
              Total Price (Net): <strong>{numToCurrency.format(getTotalNetPrice())}</strong>
            </p>
          </Box>
          <p>
            <div>Price Per SF{netLabel}:</div>
            {filteredDeal && (filteredDeal.cancelled.dateCancelled || filteredDeal.rescission.dateRescinded) ? (
              <div>
                <strong>{numToCurrency.format(Math.round(displayPrice(filteredDeal.basePrice) / unit.size))}</strong>
              </div>
            ) : (
              <div>
                <strong>{numToCurrency.format(Math.round(displayPrice(unit.basePrice) / unit.size))}</strong>
              </div>
            )}
          </p>
          {!project.netHst ? (
          <Box sx={{ mt: 1 }}>
            {filteredDeal && (filteredDeal.cancelled.dateCancelled || filteredDeal.rescission.dateRescinded) ? (
              <div>
                Effective Unit Price After HST Rebate:{' '}
                <p>
                  <strong>
                    {unit.type === 'commercial'
                      ? 'N/A'
                      : numToCurrencyDecimal.format(effectivePriceAfterRebate(filteredDeal.basePrice, project))}
                  </strong>
                </p>
              </div>
            ) : (
              <div>
                <p>
                  Effective Unit Price After HST Rebate:{' '}
                  <strong>
                    {unit.type === 'commercial' ? 'N/A' : numToCurrencyDecimal.format(effectivePriceAfterRebate(unit.basePrice, project))}
                  </strong>
                </p>
              </div>
            )}
          </Box>
          ) : null}
          <Box sx={{ mt: 1 }}>
            {filteredDeal && (filteredDeal.cancelled.dateCancelled || filteredDeal.rescission.dateRescinded) ? (
              <div>
                Total HST Rebate Amount from Unit Price:{' '}
                <p>
                  <strong>
                    {unit.type === 'commercial'
                      ? 'N/A'
                      : numToCurrency.format(hstRebate(filteredDeal.basePrice, project))}
                  </strong>
                </p>
              </div>
            ) : (
              <div>
                <p>Total HST Rebate Amount from Unit Price:{' '}
                <strong>
                  {unit.type === 'commercial' ? 'N/A' : numToCurrency.format(hstRebate(unit.basePrice, project))}
                </strong>
                </p>
              </div>
            )}
          </Box>
          {typeof unit.rental !== 'undefined' && unit.rental !== null ? (
            <p>
              Rental: <strong>{numToCurrency.format(unit.rental)}</strong>
            </p>
          ) : null}
          <p>
            Level: <strong>{unit.level}</strong>
          </p>
          <p>
            Unit: <strong>{unit.unit}</strong>
          </p>
          <p>
            Size: <strong>{unit.size}</strong>
          </p>
          {unit.ceilingHeight ? (
            <p>
              Ceiling Height: <strong>{unit.ceilingHeight}</strong>
            </p>
          ) : null}
          <p>
            Model Type: <strong>{unit.modelType}</strong>
          </p>
          <p>
            Unit Type: <strong>{unit.unitType}</strong>
          </p>
          <p>
            Bathrooms: <strong>{unit.bathroom}</strong>
          </p>
          <p>
            Exposure: <strong>{unit.exposure}</strong>
          </p>
          <p>
            Outdoor Size: <strong>{getOutdoorSize(unit.outdoor)}</strong>
          </p>
          <p>
            Outdoor Type: <strong>{getOutdoorType(unit.outdoor) || 'N/A'}</strong>
          </p>
          <p>
            Maintenance Fee: <strong>{unit.maintenanceFee ? numToCurrencyDecimal.format(unit.maintenanceFee) : 'N/A'}</strong>
          </p>
          <p>
            First Tentative Occupancy:{' '}
            <strong>{unit.firstTentativeOccupancy ? convertAllDates(unit.firstTentativeOccupancy, 'PP') : 'N/A'}</strong>
          </p>
          <p>
            Final Tentative Occupancy:{' '}
            <strong>{unit.finalTentativeOccupancy ? convertAllDates(unit.finalTentativeOccupancy, 'PP') : 'N/A'}</strong>
          </p>
          <p>
            Firm Occupancy: <strong>{unit.firmOccupancy ? convertAllDates(unit.firmOccupancy, 'PP') : 'N/A'}</strong>
          </p>
          <p>
            Outside Occupancy: <strong>{unit.outsideOccupancy ? convertAllDates(unit.outsideOccupancy, 'PP') : 'N/A'}</strong>
          </p>
          {unit.custom ? (
            <p>
              Custom Changes: <strong>{unit.custom}</strong>
            </p>
          ) : null}
        </Box>
      ) : (
        <form id="unitForm" onSubmit={handleSaveUnit}>
          <Unit
            unit={unit}
            level={level}
            setLevel={setLevel}
            unitNumber={unitNumber}
            setUnitNumber={setUnitNumber}
            rental={rental}
            setRental={setRental}
            size={size}
            setSize={setSize}
            modelType={modelType}
            setModelType={setModelType}
            handleDropdown={handleDropdown}
            unitType={unitType}
            bathroom={bathroom}
            setBathroom={setBathroom}
            exposure={exposure}
            outdoorSize={outdoorSize}
            setOutdoorSize={setOutdoorSize}
            outdoorType={outdoorType}
            maintenanceFee={maintenanceFee}
            setMaintenanceFee={setMaintenanceFee}
          />
          {project._id === '65bd404201d843802bb37a51' ? (
            <Autocomplete
              options={[...new Set(units.map((unit: IUnit) => unit.projectName))]}
              getOptionLabel={(option: any) => option}
              title={'Project Name'}
              value={projectName}
              freeSolo={true}
              onChange={(e, value: any) => setProjectName(value)}
              onInputChange={(e, value, reason) => {
                setProjectName(value);
              }}
              renderInput={(params) => <TextField {...params} sx={{ mt: 2 }} variant="outlined" label="Project Name" />}
            />
          ) : null}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              views={['year', 'month', 'day']}
              label={'First Tentative Occupancy Date (YYYY/MM/DD)'}
              value={firstTentativeOccupancy ? new Date(firstTentativeOccupancy) : null}
              onChange={(newValue) => setFirstTentativeOccupancy(newValue)}
              sx={{ mt: 2 }}
            />

            <DatePicker
              views={['year', 'month', 'day']}
              label={'Final Tentative Occupancy Date'}
              value={finalTentativeOccupancy ? new Date(finalTentativeOccupancy) : null}
              onChange={(newValue) => setFinalTentativeOccupancy(newValue)}
              sx={{ mt: 2 }}
            />
            <DatePicker
              views={['year', 'month', 'day']}
              label={'Firm Occupancy Date'}
              value={firmOccupancy ? new Date(firmOccupancy) : null}
              onChange={(newValue) => setFirmOccupancy(newValue)}
              sx={{ mt: 2 }}
            />
            <DatePicker
              views={['year', 'month', 'day']}
              label={'Outside Occupancy Date'}
              value={outsideOccupancy ? new Date(outsideOccupancy) : null}
              onChange={(newValue) => setOutsideOccupancy(newValue)}
              sx={{ mt: 2 }}
            />
          </LocalizationProvider>
        </form>
      )}
    </Box>
  );
};

const UPDATEUNIT = gql`
  mutation unitUpdateById($_id: MongoID!, $record: UpdateByIdUnitInput!) {
    unitUpdateById(_id: $_id, record: $record) {
      record {
        _id
        suite
        unit
        projectName
        level
        modelType
        basePrice
        size
        status
        getUrl
        putUrl
        bathroom
        rental
        exposure
        status
        outdoor {
          name
          size
        }
        maintenanceFee
        unitType
        originalPrice
        basePrice
        custom
        tier
        type
        firstTentativeOccupancy
        finalTentativeOccupancy
        firmOccupancy
        outsideOccupancy
        allocation {
          _id
          fullName
        }
        history {
          type
          description
          timestamp
          user {
            _id
            fullName
          }
          _id
        }
      }
    }
  }
`;

const UNITS = gql`
  query getUnits($project: MongoID!, $statuses: [String]) {
    getUnits(project: $project, statuses: $statuses) {
      projectName
    }
  }
`;

export default UnitInfo;
